import axios, { AxiosError, AxiosInstance } from 'axios'
import { ErrorHandler } from '../lib/errorHandler'
import { navigateToLogin } from '../lib/navigation'
import i18n from '../lib/i18n'
import { toast } from '../lib/toast'
import { getConfig } from '../config'

// Lazy axios instance - initialized after config loads
let apiInstance: AxiosInstance | null = null

/**
 * Get or create the axios instance
 * Must be called after config is loaded
 */
function getApiInstance(): AxiosInstance {
  if (!apiInstance) {
    const config = getConfig()
    apiInstance = axios.create({
      baseURL: config.apiUrl,
      timeout: 120000, // Default 2 minutes (individual requests can override, e.g., content generation uses 8 minutes)
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Set up interceptors
    setupInterceptors(apiInstance)
  }
  return apiInstance
}

// Export api - will be initialized when first used (after config loads)
export const api = new Proxy({} as AxiosInstance, {
  get(_target, prop) {
    return getApiInstance()[prop as keyof AxiosInstance]
  },
  set(_target, prop, value) {
    getApiInstance()[prop as keyof AxiosInstance] = value as any
    return true
  },
}) as AxiosInstance

// Token storage utilities (moved here to avoid circular dependency)
const TOKEN_KEYS = {
  ACCESS: 'access_token',
  REFRESH: 'refresh_token',
} as const

export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(TOKEN_KEYS.ACCESS),
  getRefreshToken: () => localStorage.getItem(TOKEN_KEYS.REFRESH),
  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem(TOKEN_KEYS.ACCESS, accessToken)
    localStorage.setItem(TOKEN_KEYS.REFRESH, refreshToken)
    // Reset the flag and timestamp when new tokens are set (user logged in)
    hasShownSessionExpired = false
    lastSessionExpiredTime = 0
  },
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEYS.ACCESS)
    localStorage.removeItem(TOKEN_KEYS.REFRESH)
    // Reset the flag and timestamp when tokens are cleared
    hasShownSessionExpired = false
    lastSessionExpiredTime = 0
  },
}

// Token refresh mutex to prevent race conditions
// When multiple requests fail with 401 simultaneously, only ONE refresh should happen
let isRefreshing = false
let refreshSubscribers: {
  resolve: (token: string) => void
  reject: (error: Error) => void
}[] = []

// Flag to prevent showing multiple "session expired" error messages
// When multiple requests fail with 401 simultaneously, only show error once
let hasShownSessionExpired = false
let lastSessionExpiredTime = 0
const SESSION_EXPIRED_DEBOUNCE_MS = 2000 // Don't show duplicate errors within 2 seconds

// Subscribe to refresh completion (returns a promise that resolves with new token or rejects)
const waitForRefresh = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    refreshSubscribers.push({ resolve, reject })
  })
}

// Notify all subscribers that refresh completed successfully
const onRefreshSuccess = (token: string) => {
  refreshSubscribers.forEach(({ resolve }) => resolve(token))
  refreshSubscribers = []
}

// Notify all subscribers that refresh failed
const onRefreshFailure = (error: Error) => {
  refreshSubscribers.forEach(({ reject }) => reject(error))
  refreshSubscribers = []
}

// Token refresh function with mutex - exported for use in auth.ts
export const refreshTokenWithMutex = async (): Promise<string> => {
  const refreshTokenValue = tokenStorage.getRefreshToken()
  if (!refreshTokenValue) {
    throw new Error('No refresh token available')
  }

  // If already refreshing, wait for the current refresh to complete
  if (isRefreshing) {
    return waitForRefresh()
  }

  isRefreshing = true

  try {
    const response = await getApiInstance().post('/auth/refresh', { 
      refresh_token: refreshTokenValue 
    })

    if (response.data?.access_token && response.data?.refresh_token) {
      tokenStorage.setTokens(response.data.access_token, response.data.refresh_token)
      const newToken = response.data.access_token
      onRefreshSuccess(newToken)
      return newToken
    } else {
      const error = new Error('Invalid token refresh response')
      onRefreshFailure(error)
      throw error
    }
  } catch (error) {
    // Notify all waiting requests that refresh failed
    onRefreshFailure(error instanceof Error ? error : new Error('Token refresh failed'))
    throw error
  } finally {
    isRefreshing = false
  }
}

function setupInterceptors(instance: AxiosInstance) {
  // Request interceptor - adds auth token to ALL requests
  instance.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // Log request details for checkout session
    if (config.url?.includes('/api/payments/create-checkout-session')) {
      // console.log('🌐 API Interceptor - Checkout Session Request');
      // console.log('🌐 Method:', config.method);
      // console.log('🌐 URL:', config.url);
      // console.log('🌐 Base URL:', config.baseURL);
      // console.log('🌐 Full URL:', `${config.baseURL}${config.url}`);
      // console.log('🌐 Headers:', config.headers);
      // console.log('🌐 Data:', config.data);
      // console.log('🌐 Data stringified:', JSON.stringify(config.data));
    }
    return config
  },
  (error) => Promise.reject(error)
)

  // Response interceptor - handles errors globally for ALL requests
  instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config
    
    // Auth endpoints handle their own errors (wrong credentials, unverified account, etc.)
    // They must NOT trigger the token refresh flow or global error toasts — their errors
    // belong to the calling component (login form, register form) which displays the correct
    // message to the user
    const isAuthEndpoint =
      originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/register') ||
      originalRequest?.url?.includes('/auth/google') ||
      originalRequest?.url?.includes('/auth/verify') ||
      originalRequest?.url?.includes('/auth/forgot-password') ||
      originalRequest?.url?.includes('/auth/reset-password')

    // Handle 401 - Session expired
    if (error.response?.status === 401) {
      const isRefreshEndpoint = originalRequest?.url?.includes('/auth/refresh')

      if (isAuthEndpoint) {
        return Promise.reject(error)
      }

      const now = Date.now()
      
      // Check both flag and timestamp to prevent race conditions and rapid duplicate errors
      // Set flag IMMEDIATELY to prevent race condition when multiple requests fail simultaneously
      // This must be done synchronously before any async operations
      const timeSinceLastError = now - lastSessionExpiredTime
      const shouldShowError = !hasShownSessionExpired && timeSinceLastError > SESSION_EXPIRED_DEBOUNCE_MS
      
      if (shouldShowError) {
        hasShownSessionExpired = true
        lastSessionExpiredTime = now
      }
      
      // If refresh endpoint failed, force logout (don't retry refresh)
      if (isRefreshEndpoint) {
        tokenStorage.clearTokens()
        navigateToLogin()
        // Only show error message once, even if multiple requests fail
        if (shouldShowError) {
          toast.raw.error(i18n.t('notifications:errors.sessionExpired'))
        }
        return Promise.reject(error)
      }
      
      // Prevent infinite retry loops - only retry once per request
      if (originalRequest && !(originalRequest as any)._retry) {
        (originalRequest as any)._retry = true
        
        try {
          // Try to refresh the token (mutex ensures only one refresh happens)
          const newToken = await refreshTokenWithMutex()
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return instance.request(originalRequest)
        } catch (refreshError) {
          // Refresh failed, force logout
          tokenStorage.clearTokens()
          navigateToLogin()
          // Only show error message once, even if multiple requests fail
          if (shouldShowError) {
            toast.raw.error(i18n.t('notifications:errors.sessionExpired'))
          }
          return Promise.reject(refreshError)
        }
      }
      
      // Already retried once, don't retry again
      return Promise.reject(error)
    }
    
    // Handle 404 - Not Found (suppress for expected missing endpoints)
    if (error.response?.status === 404) {
      const url = error.config?.url || ''
      const isExpected404 = url.includes('/user/email-status') || 
                           url.includes('/user/current-plan') || 
                           url.includes('/user/subscription-status') ||
                           url.includes('/content-purposes') ||
                           url.includes('/lookups/content-purposes') ||
                           url.includes('/api/payments/subscription-status')
      
      if (!isExpected404) {
        ErrorHandler.handle(new Error('Resource not found. Please check the URL.'), 'API')
      }
      // For expected 404s, just reject without showing toast
      return Promise.reject(error)
    }
    
    // Check if request opted out of global error toast (component will handle errors)
    const skipGlobalErrorToast = error.config?.skipGlobalErrorToast === true
    
    // Handle 403 - Forbidden
    if (error.response?.status === 403) {
      // Auth endpoints (e.g. login 403 = email not verified) handle their own errors
      if (isAuthEndpoint) return Promise.reject(error)

      // Extract detailed error message from backend if available
      const errorDetail = error.response?.data?.detail || error.response?.data?.message
      const errorMessage = errorDetail 
        ? (typeof errorDetail === 'string' ? errorDetail : 'You do not have permission to perform this action.')
        : 'You do not have permission to perform this action.'
      
      if (!skipGlobalErrorToast) {
        ErrorHandler.handle(new Error(errorMessage), 'API')
      }
    }
    
    // Handle 400 - Bad Request (validation errors, etc.)
    if (error.response?.status === 400) {
      // Extract detailed error message from backend if available
      const errorDetail = error.response?.data?.detail || error.response?.data?.message
      const errorMessage = errorDetail 
        ? (typeof errorDetail === 'string' ? errorDetail : 'Invalid request. Please check your input.')
        : 'Invalid request. Please check your input.'
      
      if (!skipGlobalErrorToast) {
        ErrorHandler.handle(new Error(errorMessage), 'API')
      }
    }
    
    // Handle 500+ - Server errors
    if (error.response?.status && error.response.status >= 500) {
      // Extract detailed error message from backend if available
      const errorDetail = error.response?.data?.detail || error.response?.data?.message
      const errorMessage = errorDetail 
        ? (typeof errorDetail === 'string' ? errorDetail : 'Server error. Please try again later.')
        : 'Server error. Please try again later.'
      
      if (!skipGlobalErrorToast) {
        ErrorHandler.handle(new Error(errorMessage), 'API')
      }
    }
    
    // Handle network errors (including timeout) – use user language for messages
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        if (!skipGlobalErrorToast) {
          const timeoutMessage = i18n.t('content:generation.errors.requestTimeout')
          ErrorHandler.handle(new Error(timeoutMessage), 'API')
        }
      } else {
        if (!skipGlobalErrorToast) {
          const networkMessage = i18n.t('common:errors.network')
          ErrorHandler.handle(new Error(networkMessage), 'API')
        }
      }
    }
    
    return Promise.reject(error)
  }
  )
}
